import { checkExpire } from '@asd-stan/helpers/app-utils';

/**
 * Fetches a stored file through the API instead of the presigned storage link
 * the GraphQL schema hands out: the bucket has no CORS rules, so the browser is
 * not allowed to read those links with fetch. The API sends CORS headers and
 * reads the bytes from storage itself.
 *
 * `path` is relative to REACT_APP_FILE_STORAGE_URL, e.g. `user-exports/<uuid>`.
 */
export const fetchApiFileBlob = async (path: string, signal?: AbortSignal): Promise<Blob> => {
	await checkExpire();

	const { accessToken }: Storage = localStorage;
	const response = await fetch(`${process.env.REACT_APP_FILE_STORAGE_URL}/${path}`, {
		signal,
		headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
	});

	if (!response.ok) {
		throw new Error(`Failed to download ${path}: ${response.status}`);
	}

	// An invalid or revoked token is rejected by the auth middleware, and the
	// shared exception handler reports that as HTTP 200 with a JSON error
	// envelope. Without this guard the envelope would be saved as if it were
	// the file itself.
	if ((response.headers.get('content-type') ?? '').startsWith('application/json')) {
		const body = await response.clone().text();
		if (body.startsWith('{"errors"')) {
			throw new Error(`${path} came back as an API error instead of a file`);
		}
	}

	return response.blob();
};
